<template>
  <div>
    <b-row>
      <b-col sm="12" class="mb-3">
        <b>ASSET LOCATION MAP</b>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="4" sm="6" class="my-1">
        <b-form-group label="Asset Type">
          <AsyncPaginatedSelect
            cache-id="map-asset-type"
            :cache-key="(company && company.id) || ''"
            name="Asset Type"
            :fetch-page="fetchAssetTypePage"
            v-model="filterBy.assetType"
            v-validate="'selectRequired'"
          />
        </b-form-group>
      </b-col>
      <b-col md="4" sm="6" class="my-1">
        <b-form-group label="Connection" description="Company">
          <AsyncPaginatedSelect
            cache-id="map-connection-company"
            :cache-key="(company && company.id) || ''"
            name="Company"
            :fetch-page="fetchConnectionCompanyPage"
            v-model="filterBy.company"
            v-validate="'selectRequired'"
          />
        </b-form-group>
      </b-col>
      <b-col md="4" sm="6" class="my-1">
        <b-form-group description="Storage Location" class="field-without-label">
          <AsyncPaginatedSelect
            cache-id="map-storage-location"
            :cache-key="(filterBy.company && filterBy.company.id) || ''"
            name="Storage Location"
            :fetch-page="fetchStorageLocationPage"
            v-model="filterBy.storageLocation"
            v-validate="'selectRequired'"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <template v-if="!currentLocation.location">
      <b-alert v-if="currentLocation.isGettingLocation" show>
        Getting your location...
      </b-alert>
      <b-alert v-if="currentLocation.error" variant="danger" show>
        Sorry, but the following error occurred:
        <strong>{{ currentLocation.error }}</strong>
      </b-alert>
    </template>
    <template v-else>
      <gmap-map
        :center="center"
        :zoom="12"
        :style="{ width: '100%', height: '400px' }"
        :options="mapConfig"
      >
        <gmap-marker
          v-for="(marker, index) in markers"
          :key="index"
          :position="marker.position"
          :clickable="true"
          @click="toggleInfoWindow(index)"
        >
          <gmap-info-window @closeclick="closeInfoWindow(index)" :opened="marker.visible">
            <b class="font-weight-bold">{{ marker.label.text }}</b>

            <div class="total-assets">
              Total Assets: <b>{{ getTotal(marker) }}</b>
            </div>

            <div class="distribution" v-show="marker.assetDistribution.total > 0">
              Distribution:
              <div
                v-for="(assetType, index) in marker.assetDistribution.assetTypes"
                :key="index"
              >
                <i class="icon-info"></i>&nbsp;
                <b>{{ assetType.assetType + ": " + assetType.perAssetTypeTotalCount }}</b>
              </div>
            </div>
          </gmap-info-window>
        </gmap-marker>
      </gmap-map>
      <span class="total-display"
        >Search Results: {{ assetLocationFilteredOptions.length }}</span
      >
    </template>
  </div>
</template>

<script>
// Utils
import { LocationUtil } from "@/utils/locationUtil";
import { DropDownItemsUtil } from "@/utils/dropDownItemsUtil";

// API
import connectionApi from "@/api/connectionApi";
import storageLocationApi from "@/api/storageLocationApi";
import assetTypeApi from "@/api/assetTypeApi";

// Components
import AsyncPaginatedSelect from "@/components/AsyncPaginatedSelect";

// Others
import config from "@/config/env-constants";
import _ from "lodash";

export default {
  name: "google-map",
  components: {
    AsyncPaginatedSelect,
  },
  props: {
    assetLocationOverlayStr: {
      type: String,
      required: true,
    },
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      center: {},
      markers: [],
      filterBy: {
        assetType: { ...config.assetTypeDefaultValue },
        company: { ...config.companyDefaultValue },
        storageLocation: { ...config.storageLocationDefaultValue },
      },
      mapConfig: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUi: false,
      },
      currentLocation: {
        isGettingLocation: false,
        location: null,
        error: null,
      },

      assetLocationOverlay: null,
      loggedUser: this.$store.getters.loggedUser,
      loggedUserCompany: this.$store.getters.loggedUserCompany,
      isSuperAdmin: this.$store.getters.isSuperAdmin,
    };
  },
  computed: {
    assetLocationFilteredOptions() {
      return this.getAssetLocationOptions();
    },
    view() {
      return this.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY;
    },
  },
  watch: {
    assetLocationFilteredOptions: {
      handler(value) {
        this.loadMarkers(value);
      },
      immediate: true,
    },
    "filterBy.company"() {
      let companyId =
        this.filterBy.company && this.filterBy.company.id
          ? this.filterBy.company.id
          : null;

      if (companyId) {
        this.$nextTick(
          () => (this.center = this.setPinOnCenterView(this.markers, companyId))
        );
      }
      this.filterBy.storageLocation = { ...config.storageLocationDefaultValue };
    },
  },
  async mounted() {
    if (!_.isEmpty(this.assetLocationOverlayStr)) {
      this.assetLocationOverlay = JSON.parse(this.assetLocationOverlayStr);
      // parse address
      _.forEach(this.assetLocationOverlay, (overlay) => {
        overlay.geoaddress = LocationUtil.getGeoaddress(overlay.geoaddress);
      });
    }

    await this.findMyLocation();
  },
  methods: {
    async fetchAssetTypePage(search, page) {
      if (!this.company || !this.company.id) {
        return { items: [], hasMore: false };
      }

      let filterBy = { company: { id: this.company.id }, companyId: this.company.id };
      let { data } = await assetTypeApi.getAssetTypes(
        filterBy,
        this.view,
        this.loggedUser.id,
        { search, page, pageSize: 20 }
      );
      let items = _.map(data.assetTypes, (assetType) => ({
        value: { id: assetType.id, name: assetType.name, origin: assetType.origin, originId: assetType.originId },
        text: assetType.name,
      }));
      return { items, hasMore: !!(data.pageInfo && data.pageInfo.hasMore) };
    },
    async fetchConnectionCompanyPage(search, page) {
      if (!this.company || !this.company.id) {
        return { items: [], hasMore: false };
      }

      let items = [];
      if (page === 1) {
        items.push({
          value: DropDownItemsUtil.getCompanyItem(this.company),
          text: DropDownItemsUtil.getCompanyDisplay(this.company),
        });
      }

      let filterBy = {
        company: { id: this.company.id },
        companyAccess: this.loggedUser.companyAccess,
      };
      let { data } = await connectionApi.getConnections(
        filterBy,
        this.view,
        this.loggedUser.id,
        { search, page, pageSize: 20 }
      );
      _.forEach(data.connections, (connection) => {
        items.push({
          value: DropDownItemsUtil.getCompanyItem(connection.connectedCompany),
          text: DropDownItemsUtil.getCompanyDisplay(connection.connectedCompany),
        });
      });

      return { items, hasMore: !!(data.pageInfo && data.pageInfo.hasMore) };
    },
    async fetchStorageLocationPage(search, page) {
      let companyId =
        this.filterBy.company && this.filterBy.company.id
          ? this.filterBy.company.id
          : null;
      if (!companyId) {
        return { items: [], hasMore: false };
      }

      let filterBy = {
        company: { id: companyId },
        companyAccess: this.loggedUser.companyAccess,
      };
      let { data } = await storageLocationApi.getStorageLocations(
        filterBy,
        this.view,
        this.loggedUser.id,
        { search, page, pageSize: 20 }
      );
      let items = _.map(data.storageLocations, (loc) => ({
        value: DropDownItemsUtil.getStorageLocationItem(loc),
        text: DropDownItemsUtil.getStorageLocationDisplay(loc),
      }));
      return { items, hasMore: !!(data.pageInfo && data.pageInfo.hasMore) };
    },
    getTotal(marker) {
      return !marker.assetDistribution.total ? 0 : marker.assetDistribution.total;
    },
    getAssetLocationOptions() {
      let data = [];

      if (!_.isEmpty(this.assetLocationOverlay)) {
        data = this.assetLocationOverlay;

        if (this.filterBy.assetType && this.filterBy.assetType.id) {
          data = data.filter((x) => {
            let hasAssetType = _.find(x.assetTypes, (o) => {
              return o.assetTypeId === this.filterBy.assetType.id;
            });
            return !_.isEmpty(hasAssetType);
          });
        }

        if (this.filterBy.company && this.filterBy.company.id) {
          data = this.assetLocationOverlay.filter(
            (x) => x.connectedCompanyId === this.filterBy.company.id
          );
        }

        if (this.filterBy.storageLocation && this.filterBy.storageLocation.id) {
          data = data.filter(
            (x) => x.connectedStorageLocationId === this.filterBy.storageLocation.id
          );
        }
      }

      return data;
    },
    loadMarkers(markers) {
      // reset all markers
      this.markers = [];

      if (markers && !_.isEmpty(markers)) {
        for (const marker of markers) {
          this.addMarker(marker);
        }
      }

      this.closeAllInfoWindows();
    },
    addMarker(marker) {
      let geoaddress = marker.geoaddress;

      let geolocation = {
        lat: geoaddress.latitude,
        lng: geoaddress.longitude,
      };

      this.markers.push({
        position: geolocation,
        assetDistribution: marker,
        visible: false,
        label: {
          text: marker.connectedStorageLocation,
          color: "#4285F4",
          fontSize: "12px",
          fontWeight: "bold",
        },
      });

      this.center = geolocation;
    },
    toggleInfoWindow(index) {
      let prevValue = this.markers[index].visible;
      this.markers[index].visible = !prevValue;
    },
    closeInfoWindow(index) {
      this.toggleInfoWindow(index);
    },
    closeAllInfoWindows() {
      for (const marker of this.markers) {
        if (marker.visible) marker.visible = false;
      }
    },
    setPinOnCenterView(markers, companyId) {
      let centerMarkerCoordinates = {};

      for (const item of markers) {
        let distribution = item.assetDistribution;
        if (this.isSuperAdmin) {
          if (distribution.companyId === distribution.connectedCompanyId) {
            centerMarkerCoordinates = {
              lat: distribution.geoaddress.latitude,
              lng: distribution.geoaddress.longitude,
            };
            break;
          }
        } else {
          let lat = parseFloat(distribution.geoaddress.latitude);
          let lng = parseFloat(distribution.geoaddress.longitude);

          if (
            (companyId && companyId === distribution.connectedCompanyId) ||
            distribution.companyId === distribution.connectedCompanyId
          ) {
            centerMarkerCoordinates = { lat, lng };
            break;
          }
        }
      }

      return centerMarkerCoordinates;
    },
    async findMyLocation() {
      this.currentLocation.isGettingLocation = true;
      try {
        this.currentLocation.isGettingLocation = false;
        this.currentLocation.location = await this.$getCurrentLocation();
        const lat = parseFloat(this.currentLocation.location.lat);
        const lng = parseFloat(this.currentLocation.location.lng);
        this.center = { lat, lng };

        if (this.markers.length > 0 && this.company.id) {
          this.center = this.setPinOnCenterView(this.markers, this.company.id);
        }
      } catch (error) {
        this.currentLocation.isGettingLocation = false;

        switch (error.code) {
          case error.PERMISSION_DENIED:
            this.currentLocation.error = "User denied the request for Geolocation.";
            break;
          case error.POSITION_UNAVAILABLE:
            this.currentLocation.error =
              "Location information is unavailable. Please try to enable your Location Settings.";
            break;
          case error.TIMEOUT:
            this.currentLocation.error =
              "The request to get user location timed out. Please try to enable your Location Settings.";
            break;
          case error.UNKNOWN_ERROR:
            this.currentLocation.error = "An unknown error occurred.";
            break;
        }
      }
    },
  },
};
</script>

<style scoped>
.total-assets {
  margin-top: 5px;
}

.distribution {
  margin-top: 5px;
}

.field-without-label {
  margin-top: 30px;
}
</style>
